<template>
  <div class="chat-container" @click="updateUserActivity" @keydown="updateUserActivity" :style="{ '--branding-color': brandingColor }">
    <ChatHeader
      visibilityOfPrechatForm="visibilityOfPrechatForm"
      @handleChatSize="handleChatSize"
      @handleChatMinimize="handleChatMinimize"
      @closeWidgetEvent="closeWidgetEvent"
    />
    <ChatModals
      :showCloseModal="showCloseModal"
      :showEndSessionModal="showEndSessionModal"
      :showAdditionalModal="showAdditionalModal"
      @endChat="endChat"
      @showModalClose="showModalClose"
      @showAdditionalModalClose="showAdditionalModalClose"
    />
    <section class="chat" ref="chat" :style="{'display': chatMinimizedState}" >
      <ErrorMessage v-if="error" :message="error" />
    <PreChatFormContainer
      :visibilityOfPrechatForm="visibilityOfPrechatForm"
      :prechatList="prechatList"
      :error="error"
      :privacyDisclaimer="privacyDisclaimer"
      :userDetails="userDetails"
      :hasHistory="hasHistory"
      @cancelChat="cancelChat"
      @cancelToaster="cancelToaster"
      @proccedToPrechat="proccedToPrechat"
      @startChat="startChat"
      @startInitiateChat="startInitiateChat"
    />
    <ChatContainer
       :messages="messages"
       :loading="loading"
       @submitMidChatForm="submitMidChatForm"
       :userDetails="userDetails"
      />

      <RichSuggestion
          v-for="suggestion in suggestions.textSuggestions"
          :key="suggestion.title"
          :alignment="suggestions.alignment"
          :title="suggestion.title"
          @select="send({text: suggestion.title, chipSelection: true})"
      />

      <RichSuggestion
          v-for="suggestion in suggestions.linkSuggestions"
          :key="suggestion.title"
          :alignment="suggestions.alignment"
          :title="suggestion.title"
          :uri="suggestion.url"
      />
    </section>

    <ChatField
        v-if="(hasConnection || hasHistory) && !endConversation"
        ref="input"
        :style="{'display': chatMinimizedState}"
        :lang="lang"
        :showUploader="showUploader"
        :disabledtextbox="this.textboxDisbled"
        @submit="send"
        @listening="stop_feedback"
        @input="startTyping"
        @blur="stopTyping"
        @typing="stop_feedback"
        @openAdditionalModal="showAdditionalModal = true; showCloseModal = false"
        :agentTyping="agentTyping"
        @disableSoftSearch = "disableSoftSearch"
    />
  </div>
</template>

<script>
import userAdditionaldata from '../mixins/userAdditionaldata'
import ErrorMessage from '@/components/common/ErrorMessage.vue'
import ChatField from '@/components/common/ChatField.vue'
import RichSuggestion from '@/components/RichSuggestion.vue'
import ChatHeader from '@/components/ChatComponents/ChatHeader.vue'
import ChatModals from '@/components/ChatComponents/ChatModals.vue'
import PreChatFormContainer from '@/components/forms/PreChatFormContainer.vue'
import { messageTypes, uiToggleConfigs } from '@/utils/constants'
import ChatContainer from '@/components/ChatComponents/ChatContainer.vue'
import config from '@/config'
import translations from '@/translations/translations.json'
import { connectSocketApi } from '@/services/websockets'
import { formatUpperCaseToCapitalize } from '@/utils/helpers'
import { mapActions, mapState } from 'pinia'
import { useWidgetStore } from '@/store'
import {
  chatConnectedTracking, chatContinuedTracking, chatbotSelectTopicTracking, chatCompletedTracking, chatCloseTracking
  , chatSuccessfulTracking, chatFailureTracking, agentHandoffTracking
} from '@/utils/analytics'
import { hasChatDigitalData, removeChatDigitalDataLocalStorage, setChatDigitalData } from '@/utils/medalliaUtil'

export default {
  mixins: [userAdditionaldata],
  name: 'App',
  components: {
    ErrorMessage,
    ChatField,
    RichSuggestion,
    ChatHeader,
    ChatModals,
    PreChatFormContainer,
    ChatContainer
  },
  props: {
    prechatList: {
      type: Array,
      default: () => ([])
    },
    privacyDisclaimer: {
      type: String,
      default: ' '
    },
    toasterConfigData: {
      type: Object,
      default: () => ({})
    },
    liveChatPage: {
      type: Boolean,
      default: false
    },
    userDetails: {
      type: Object,
      default: () => ({})
    },
    IVRAgents: {
      type: Array,
      default: () => ([])
    },
    nonIVRAgents: {
      type: Array,
      default: () => ([])
    },
  },
  data () {
    return {
      translations,
      messageTypes,
      messages: [],
      muted: true,
      error: null,
      audio: new Audio(),
      chatExpand: false,
      showCloseModal: false,
      showAdditionalModal: false,
      loading: false,
      typingMessage: false,
      hasConnection: false,
      webSocket: null,
      handleCloseByUser: false,
      UILang: localStorage.getItem('lang'),
      toasterTimeout: null,
      pingInterval: null,
      visibilityOfPrechatForm: false,
      endConversation: false,
      showEndSessionModal: false,
      sessionTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds,
      intervalIdTermination: null,
      modalCloseTimer: null,
      broadcastChannel: null,
      isTyping: false,
      typingTimer: null,
      agentTypingTimer: null,
      toasterConfigDataValue: this.toasterConfigData,
      toasterExpireTime: 300000,
      MidChatFormSumitted: false,
      textboxDisbled: false,
      textboxDisledToggle: true,
      agentTyping: false
    }
  },
  computed: {
    ...mapState(useWidgetStore, [
      'connectedToAgent',
      'sessionId',
      'participantId',
      'participantKey',
      'messageHistory',
      'brandingColor',
      'lastActivityTime',
      'chatStarted',
      'chatMinimizedState',
      'getUnreadMessages',
      'chatInitialMessage',
      'supportAnchorLinkFS',
      'darkProdFlag',
      'enableSoftSearch',
      'brandingColor'
    ]),
    hasHistory () {
      return this.messageHistory
    },
    lang () {
      return localStorage.getItem('lang')
    },
    countryCode () {
      return localStorage.getItem('country_code')
    },
    showUploader () {
      return this.connectedToAgent || false
    },
    getChipsIndex () {
      return this.messages.findLastIndex(msg => msg.type === 'CHIPS')
    },
    suggestions () {
      if (this.messages.length > 0) {
        const lastMessage = this.messages[this.messages.length - 1]
        const lastMessageWithChips = this.messages[this.getChipsIndex]
        const textSuggestions = []
        const linkSuggestions = []
        let alignment
        const isValid = this.checkMessagesValidity(this.messages)

        if (lastMessage.type === messageTypes.CHIPS) {
          alignment = lastMessage.alignment ? lastMessage.alignment : 'column'
          lastMessage.chips.forEach(el => {
            if (el.url) {
              linkSuggestions.push(el)
            } else {
              textSuggestions.push(el)
            }
          })
        } else if (isValid) {
          alignment = lastMessageWithChips.alignment ? lastMessageWithChips.alignment : 'column'
          lastMessageWithChips.chips.forEach(el => {
            if (el.url) {
              linkSuggestions.push(el)
            } else {
              textSuggestions.push(el)
            }
          })
        }
        return { textSuggestions, linkSuggestions, alignment }
      }
      return {
        textSuggestions: config.start_suggestions // <- if no messages are present, return start_suggestions, from config.js to help user figure out what he can do with your application
      }
    },
    UnreadMessages () {
      if (this.getUnreadMessages > 9) {
        return '9+'
      } else {
        return this.getUnreadMessages
      }
    }
  },
  watch: {
    /* This function is triggered, when new messages arrive */
    messages: {
      deep: true,
      handler (messages) {
        const isTerminated = messages?.find(mes => mes.text === 'EVENT_CHAT_TERMINATED')
        const isToAgent = messages?.find(mes => mes.type === 'AGENT_TRANSFER_MESSAGE')
        // toggle after QA we will disleble
        if (this.textboxDisledToggle) {
          this.textboxDisbled = messages?.find((mes) => mes?.agentExit === 'true')
        }
        if (isToAgent && !this.connectedToAgent) {
          this.setConnectedToAgentStatus(true)
          // Fetching agent type
          const agentType = messages[messages.length - 1]?.topic
          // Agent Handoff Tracking
          agentHandoffTracking(agentType)
          this.webSocket.send(JSON.stringify({
            event: 'FILE_LIMITS',
            participantKey: this.participantKey,
            participantId: this.participantId,
            sessionId: this.sessionId
          }))
        }
        const index = messages.indexOf(isTerminated)
        if (index > -1) {
          messages.splice(index, 1)
        }

        this.setMessageHistory(JSON.stringify(messages))
      }
    },
    /* If muted, stop playing feedback */
    muted (muted) {
      this.audio.muted = muted
      if (muted) this.stop_feedback()
    }
  },
  created () {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  mounted () {
    // toaster hide for mobile(small device(max-width:600))
    this.addAdditionsParameters()
    if (window.matchMedia('(max-width: 600px)').matches && this.toasterConfigDataValue.checkScreen) {
      this.toasterConfigDataValue.inviteTimeOut = 0
    }
    // Create a Broadcast Channel instance
    this.broadcastChannel = new BroadcastChannel('websocket_channel')
    // Listen for incoming messages from other tabs/windows
    this.broadcastChannel.onmessage = (event) => {
      if (event.data === 'TerminationHappened') {
        this.$emit('recheckEligibility', true)
        clearTimeout(this.toasterTimeout)
        this.$emit('setShowChat', 'hide')
      } else {
        const receivedMessage = event.data
        this.setLastActivityTime(receivedMessage)
      }
    }

    /* If history is enabled, the messages are retrieved from localStorage */
    if (this.hasHistory) {
      this.messages = JSON.parse(this.messageHistory)
      // Add class to the body to hide the live chat when gateway chat session is active
      document.querySelector('body').classList.add('active-gatewayChat')

      if (hasChatDigitalData()) {
        setChatDigitalData()
      }

      /* Chat continued tracking */
      chatContinuedTracking()
    } else {
      // Update User activity when only no session is present
      this.updateUserActivity()

      // Hide Toaster after toaster timeout
      this.toasterTimeout = setTimeout(() => {
        if ((this.sessionId === undefined || this.sessionId == null || this.sessionId === 'null') && this.chatMinimizedState === 'block') {
          // Ensured session hasn't started yet
          this.cancelChat()
          this.setChatMinimized(false)
        }
      }, this.toasterConfigDataValue?.inviteTimeOut)
    }

    this.handleSessionExpiration()

    /* Session should be persistent (in case of page reload, the context should stay) */
    if (this.sessionId !== undefined && this.sessionId !== null && this.sessionId !== 'null') {
      this.$nextTick(() => {
        this.connectSocket(true)
      })
    }

    document.addEventListener('visibilitychange', () => {
      if (this.sessionId === undefined || this.sessionId === null || this.sessionId === 'null') {
        return
      }

      if (this.agentTyping === true) {
        this.agentTyping = false
      }

      if (document.hidden) {
        console.log('WS: Tab is hidden', this.websocket?.readyState)
        this.closeListener()
      } else {
        console.log('WS: Tab is visible')
        /* Session should be persistent (in case of page reload, the context should stay) */
        if (this.hasHistory) {
          // this.handleSessionExpiration()
          this.messages = JSON.parse(this.messageHistory)

          // Setting the chat digital data in case of visibility change
          if (hasChatDigitalData()) {
            setChatDigitalData()
          }
        }
        if (this.sessionId !== undefined && this.sessionId !== null && this.sessionId !== 'null') {
          // this.$nextTick(() => {
          //   console.log('WS: Reconnecting socket(new)')
          // })
          this.connectSocket(true)
        }
      }
    })
  },
  beforeUnmount () {
    console.log('Before unmount called')
    this.closeListener()
    // Clear the ping interval
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
    }

    if (this.sessionTimeout) {
      clearInterval(this.sessionTimeout)
    }

    clearInterval(this.intervalIdTermination)
    clearInterval(this.modalCloseTimer)

    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    document.removeEventListener('mousemove', this.updateUserActivity)
    document.removeEventListener('keydown', this.updateUserActivity)

    if (this.broadcastChannel) {
      this.broadcastChannel.close()
    }
  },
  methods: {
    ...mapActions(useWidgetStore, [
      'setSessionData',
      'setFileLimits',
      'setConnectedToAgentStatus',
      'setMessageHistory',
      'setWebSocket',
      'setEligibleChat',
      'setLastActivityTime',
      'clearDataCollectionStatuses',
      'clearDataCollectionForms',
      'setChatStarted',
      'setChatMinimized',
      'setUnreadMessages',
      'setChatInitialMessage',
      'clearLoggedInUserFormData',
      'setSupportAnchorLinkFSData',
      'setDarkProdFlag',
      'setEnableSoftSearch'
    ]),
    capitalizeFirstLetter (string) {
      if (!string) {
        return
      }
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
    startChat () {
      this.$emit('startChat')
    },
    showModalClose () {
      this.showCloseModal = false
    },
    showAdditionalModalClose () {
      this.showAdditionalModal = false
    },
    getCookie (name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
      if (match) return match[2]
    },
    deleteCookie (name) {
      document.cookie = name + '=;  Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    },
    sendPing () {
      if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.send(JSON.stringify({ event: 'PING' }))
      }
    },
    openListener (exist) {
      // Pass SKU/Catalog Number in genesys cloud from Gateway
      const productSku = window.digitalData.products && window.digitalData.products.length > 0 ? window.digitalData.products[0].id || window.digitalData.products[0].catalogNumber || window.digitalData.products[0].categoryId : null
      console.log('WS: openListener called')
      this.hasConnection = true
      this.loading = false
      if (!exist) {
        console.log('WS: Chat Requested Event called')
        this.webSocket.send(JSON.stringify({
          event: 'CHAT_REQUESTED',
          browserLang: this.lang,
          browserLink: window.location.href,
          originUrl: window.location.href,
          prechatUserData: { ...this.prechatFormData, url: window.location.href },
          countryCode: this.countryCode,
          liveChat: this.liveChatPage,
          additionalUserInfo: this.additionalUserInfo,
          productSku: productSku,
          enableSearchConnex: true,
          darkChatGatewayProd: this.getCookie('darkChatGatewayProd') === 'true'
        }))
      } else if (this.webSocket && this.sessionId !== null) {
        if (this.webSocket?.readyState === 1) {
          console.log('WS: Chat Restored Event called')
          this.webSocket.send(JSON.stringify({
            event: 'CHAT_RESTORED',
            sessionId: this.sessionId,
            participantId: this.participantId,
            participantKey: this.participantKey,
            browserLink: window.location.href
          }))
        }
        this.setScrollPosition()
      }

      if (this.webSocket) {
        this.webSocket.addEventListener('message', (message) => {
          this.messageListener(message)
        })
        this.webSocket.addEventListener('error', () => {
          this.errorListener()
        })
      }
      // Start sending ping messages every 10 seconds
      this.pingInterval = setInterval(() => {
        this.sendPing()
      }, 10000)
    },
    setScrollPosition () {
      setTimeout(() => {
        const chat = document.querySelector('.chat')
        if (chat) {
          chat.scrollTo({ top: chat.scrollHeight + 300, behavior: 'smooth' })
        }
        const messageDisplay = this.$refs.chat
        if (messageDisplay) {
          messageDisplay.scrollTop = messageDisplay.scrollHeight
        }
      }, 1000)
    },
    setScrollPositionToMessage () {
      setTimeout(() => {
        const messages = document.querySelectorAll('[id^="message-client-"]')
        const lastMessage = messages[messages.length - 1]

        if (lastMessage) {
          // scrollIntoView() method scrolls the element's ancestor containers such that the element on which scrollIntoView() is called is visible to the user.
          lastMessage.scrollIntoView({ behavior: 'smooth' })
        }
        this.setScrollPosition()
      }, 200)
    },
    messageListener (message) {
      console.log('WS: Message from gateway:', message)
      const eventData = message.event || JSON.parse(message.data)
      if (eventData.event === 'CHAT_CREATED') {
        this.setSessionData({
          sessionId: eventData.sessionId,
          participantId: eventData.participantId,
          participantKey: eventData.participantKey
        })
      }
      if (eventData.event === 'MESSAGE') {
        const newData = Object.assign({ sender: 'agent', msg_index: (this.messages.filter(el => el.sender === 'agent').length + 1) }, eventData.message)
        // Is resolveInquiry key will only come when user is clicked on the resolve inquiry buttons
        if (newData?.isResolveInquiry === 'true') {
          // When the user is clicked on the resolve inquiry button - Yes
          chatSuccessfulTracking()
        } else if (newData?.isResolveInquiry === 'false') {
          // When the user is clicked on the resolve inquiry button - No
          chatFailureTracking()
        }
        if (this.chatMinimizedState === 'none') {
          // Consider as an unread message
          this.setUnreadMessages(1)
        }

        // Setting sender name in the message
        if (newData?.agentName) {
          newData.senderName = newData.agentName
        }
        if (newData?.softSearch === 'true' && uiToggleConfigs?.isSearchConnexEnabled) {
          // Enable soft search for the
          this.setEnableSoftSearch(true)
        } else if (newData?.softSearch === 'false') {
          this.setEnableSoftSearch(false)
        }

        // Setting User's name in the messaged TO DO SOLVE THE COMPLEXITY
        if (newData?.userName) {
          const FirstLettercapitalize = this.capitalizeFirstLetter(newData?.userName)
          this.setChatInitialMessage(FirstLettercapitalize + ' ' + 'said')
        } else if (newData.userName === null || newData.userName === '') {
          let userMessageInitial = this.prechatFormData?.firstName ? this.prechatFormData?.firstName + ' said' : window?.fisherDataLayer?.userData?.firstName ? window?.fisherDataLayer.userData.firstName : 'You said'
          userMessageInitial = this.capitalizeFirstLetter(userMessageInitial)
          this.setChatInitialMessage(userMessageInitial)
        }
        // Setting message time once message reached to UI
        newData.time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        this.messages.push(newData)
        if (!this.MidChatFormSumitted) {
          this.setScrollPositionToMessage(newData)
        } else {
          this.setScrollPosition()
        }
      }

      if (eventData.event === 'TYPING' && this.agentTyping === false) {
        this.agentTyping = true
        // Set a timer to send typing event after 2 seconds
        this.agentTypingTimer = setTimeout(() => {
          this.agentTyping = false
          console.log('Agent stopped typing...')
        }, 3000)
      }

      if (eventData.eventId && this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.send(JSON.stringify({
          event: 'ACK',
          sessionId: this.sessionId,
          ackEventId: eventData.eventId
        }))
      }

      if (eventData?.message?.type === messageTypes.FILE_LIMITS) {
        this.setFileLimits(eventData?.message?.userData)
      }
    },
    errorListener () {
      console.log('WS: errorListener called')
      this.error = 'Something went wrong. Please close the session or refresh the page to start a new chat.'
      this.loading = false
      console.log('WS: errorListener called')
    },
    closeListener () {
      console.log('WS: closeListener called')
      if (this.pingInterval) {
        clearInterval(this.pingInterval)
      }
      if (this.webSocket) {
        this.webSocket.close()
        this.webSocket.removeEventListener('open', this.openListener)
        this.webSocket.removeEventListener('message', this.messageListener)
        this.webSocket.removeEventListener('error', this.errorListener)
        this.webSocket.removeEventListener('close', this.closeListener)
        this.hasConnection = false
      }
    },
    connectSocket (exist) {
      console.log('WS: connectSocket conecting....', this.webSocket)
      if (!this.webSocket || this.webSocket.readyState === WebSocket.CLOSED || this.webSocket.readyState === 2) {
        // Create a new WebSocket connection if it doesn't exist or if it's closed
        this.webSocket = connectSocketApi()

        this.webSocket.addEventListener('open', () => {
          this.openListener(exist)
        })

        // Add error handler when gateway app is down during chat connect
        this.webSocket.addEventListener('error', () => {
          if (this.sessionId === undefined || this.sessionId === null || this.sessionId === 'null') {
            this.errorListener()
            this.setChatStarted(false)
          }
        })
      }
    },
    startInitiateChat (data) {
      if (data?.form) {
        this.prechatFormData = data.form
      }
      clearTimeout(this.toasterTimeout)
      clearTimeout(this.agentTypingTimer)
      this.setChatStarted(true)
      this.loading = true
      this.$nextTick(() => {
        this.connectSocket()
      })
    },
    handleChatSize () {
      this.chatExpand = !this.chatExpand
      this.$emit('resizeChat', this.chatExpand)
    },
    handleChatMinimize () {
      if (this.chatExpand) {
        this.chatExpand = false
        this.$emit('resizeChat', this.chatExpand)
      }
      const minimisedState = this.chatMinimizedState === 'block'
      this.setChatMinimized(minimisedState)
      // Restting the unread messages count when the chat is maximised
      this.setUnreadMessages(0)
      this.setScrollPosition()
    },
    send (submission) {
      console.log('WS: Message to gatway: ', submission)
      let request

      /* Text request */
      if (submission.text) {
        request = {
          event: 'MESSAGE',
          message: {
            type: messageTypes.TEXT,
            text: submission.text,
            ...(submission?.sku && { sku: submission.sku })
          },
          browserLink: window.location.href,
          participantKey: this.participantKey,
          participantId: this.participantId,
          sessionId: this.sessionId,
          languageCode: this.lang,
          countryCode: this.countryCode
        }

        this.messages.push({
          type: messageTypes.TEXT,
          text: submission.text,
          participantKey: this.participantKey,
          sender: 'client',
          msg_index: (this.messages.filter(el => el.sender === 'client').length + 1),
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        })

        if (submission?.chipSelection) {
        /* Chat connected tracking */
          if (submission.text === 'Virtual Assistant') {
            chatConnectedTracking()
          } else if (submission.text === 'Order Support' || submission.text === 'Pricing Support' || submission.text === 'Help me with Something Else' || submission.text === 'Check Order Status' || submission.text === 'Returns and Cancellations' ||
            submission.text === 'View Quote/Place Orders' || submission.text === 'Pricing' || submission.text === 'Request a Quote' || submission.text === 'View Quote/Place Orders' || submission.text === 'Account Setup' || submission.text === 'Downloading an Invoice' ||
            submission.text === 'Product Information and Documentation' || submission.text === 'Contact Customer Serivce' || submission.text === 'Order Management' ||
            submission.text === 'Registering for an Account' || submission.text === 'Report Order Issue' || submission.text === 'Place an Order/Generate a Quote' || submission.text === 'Invoice Request') {
              // For the time being we are tracking all the topics as static
              /* Chatbot select topic tracking */
                chatbotSelectTopicTracking(submission.text)
            }
        }
      } else if (submission.audio) {
        this.muted = false

        request = {
          session: this.sessionId,
          queryInput: {
            audioConfig: {
              audioEncoding: 'AUDIO_ENCODING_UNSPECIFIED',
              languageCode: this.lang
            }
          },
          inputAudio: submission.audio
        }
      } else if (submission.files) {
        request = {
          event: 'FILE',
          message: {
            type: 'FILE',
            fileList: submission.fileList
          },
          participantKey: this.participantKey,
          participantId: this.participantId,
          sessionId: this.sessionId
        }
        this.messages.push({
          type: 'FILE',
          fileList: submission.fileList,
          participantKey: this.participantKey,
          sender: 'client',
          msg_index: (this.messages.filter(el => el.sender === 'client').length + 1)
        })
        this.updateUserActivity()
      }

      this.setScrollPosition()
      this.stop_feedback()
      this.error = null

      /* Send the request to endpoint */
      console.log('WS state', this.webSocket?.readyState)
      if (this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.send(JSON.stringify(request))
      } else {
        console.log('WS: WebSocket is not open, trying to send request after delay')
        const interval = setInterval(() => {
          if (this.webSocket.readyState === WebSocket.OPEN) {
            this.webSocket.send(JSON.stringify(request))
            clearInterval(interval)
            console.log('WS: Request sent after delay, WebSocket is now open')
          }
        }, 200)
      }
      // if (this.webSocket?.readyState != 1) {
      //   // delay sending the request until the WebSocket is open
      //   setTimeout(() => {
      //     if (this.webSocket?.readyState === 1) {
      //       console.log('WS: Ready state when sending request with delay', this.webSocket?.readyState)
      //       this.webSocket.send(JSON.stringify(request))
      //     }
      //   }, 1000) // <- delay of 1 second
      // } else if (this.webSocket?.readyState === 1) {
      //   this.webSocket.send(JSON.stringify(request))
      // }
    },
    handle (response) {
      /* Handle dialog end */
      if (
        response.queryResult.diagnosticInfo &&
          response.queryResult.diagnosticInfo.end_conversation
      ) {
        this.$refs.input.disabled = true
        this.$refs.input.microphone = false
        this.$refs.input.should_listen = false
      }

      /* Speech output */
      if (response.outputAudio) {
        /* Detect MIME type (or fallback to default) */
        const mime =
            config.codecs[response.outputAudioConfig.audioEncoding] ||
            config.codecs.OUTPUT_AUDIO_ENCODING_UNSPECIFIED
        this.audio.src = `data:${mime};base64,${response.outputAudio}`
        this.audio.onended = () => this.$refs.input.listen()

        if (!this.muted) this.audio.play()
      } else {
        let text = '' // <- init a text variable

        /* Dialogflow Text/SimpleResponses */
        for (const component in response.queryResult.fulfillmentMessages) {
          if (response.queryResult.fulfillmentMessages[component].text) { text += `${response.queryResult.fulfillmentMessages[component].text.text[0]}. ` }
          if (
            response.queryResult.fulfillmentMessages[component]
              .simpleResponses &&
              response.queryResult.fulfillmentMessages[component].simpleResponses
                .simpleResponses[0].textToSpeech
          ) { text += `${response.queryResult.fulfillmentMessages[component].simpleResponses.simpleResponses[0].textToSpeech}. ` }
          if (response.queryResult.fulfillmentMessages[component].rbmText) { text += `${response.queryResult.fulfillmentMessages[component].rbmText.text}. ` }
        }

        /* Actions on Google Simple response */
        if (
          response.queryResult.webhookPayload &&
            response.queryResult.webhookPayload.google
        ) {
          for (const component in response.queryResult.webhookPayload.google
            .richResponse.items) {
            if (
              response.queryResult.webhookPayload.google.richResponse.items[
                component
              ].simpleResponse &&
                response.queryResult.webhookPayload.google.richResponse.items[
                  component
                ].simpleResponse.textToSpeech
            ) { text += `${response.queryResult.webhookPayload.google.richResponse.items[component].simpleResponse.textToSpeech}. ` }
          }
        }

        const speech = new SpeechSynthesisUtterance(text)
        speech.voiceURI = config.voice
        speech.lang = this.lang
        speech.onend = () => this.$refs.input.listen()

        if (!this.muted) window.speechSynthesis.speak(speech) // <- if app is not muted, speak out the speech
      }
    },
    /* Stop audio speech/playback */
    stop_feedback () {
      if (window.speechSynthesis) window.speechSynthesis.cancel()
      if (this.audio && !this.audio.paused) this.audio.pause()
    },
    formatSuggestions (title) {
      return formatUpperCaseToCapitalize(title)
    },
    getCarouselBrowseItems (data) {
      return data.payload.google.richResponse.items[0].carouselBrowse.items || []
    },
    getCarouselSelectItems (data) {
      return data.payload.google.richResponse.items[0].carouselSelect.items || []
    },
    getTableItems (data) {
      return data.payload.google.richResponse.items[0].tableCard || []
    },
    getFileItems (data) {
      return data.fileList
    },
    getAudioItems (data) {
      return data.payload.google.richResponse.items[0].mediaResponse.mediaObjects || []
    },
    getListItems (data) {
      if (data) {
        return data.richContent[0] || []
      }

      return []
    },
    cancelChat () {
      clearTimeout(this.toasterTimeout)
      this.$emit('setShowChat', 'hide')
      if (this.error) {
        this.error = null
      }
    },
    // this method will call only no thanks and X in the toaster popup
    cancelToaster () {
      this.$emit('toasterBrowserSession')
    },
    async terminateChatByBot () {
      setTimeout(async () => {
        this.endConversation = false
        await this.endChat() // Call your logout or session expiration function
      }, 2000)
    },
    endChatenableMedallia () {
    },
    endChat () {
      this.agentTyping = false

      this.endChatenableMedallia()
      console.log('WS: endChat called')
      if (this.webSocket && this.webSocket?.readyState === 1) {
        this.setLastActivityTime(Date.now())
        this.webSocket.send(JSON.stringify({
          event: 'CHAT_TERMINATED',
          participantKey: this.participantKey,
          participantId: this.participantId,
          sessionId: this.sessionId
        }))
        /* Chat completed tracking */
        chatCompletedTracking()

        /* Chat close tracking */
        chatCloseTracking()
        // Remove class from the body to show the live chat when gateway chat session is terminated
        document.querySelector('body').classList.remove('active-gatewayChat')
      }

      if (!this.endConversation) {
        this.messages.length = 0
        this.setMessageHistory('')
        this.showCloseModal = false
        this.$emit('closeChat')
        this.handleCloseByUser = true
        this.clearDataCollectionStatuses()
        this.clearDataCollectionForms()
      }
      this.setSessionData({
        sessionId: null,
        participantId: null,
        participantKey: null
      })

      this.setConnectedToAgentStatus(false)
      this.setFileLimits('')
      this.setEligibleChat(false)
      this.setChatStarted(false)
      this.setChatInitialMessage('You said')
      this.setEnableSoftSearch(false)
      if (this.uiToggleConfigs?.enableLoggedInUser) {
        this.clearLoggedInUserFormData()
      }
      if (this.supportAnchorLinkFS) {
        this.setSupportAnchorLinkFSData('')
      }
      if (this.sessionTimeout) {
        clearInterval(this.sessionTimeout)
      }
      this.setLastActivityTime(Date.now())
      this.showEndSessionModal = false
      clearInterval(this.intervalIdTermination)

      localStorage.removeItem('sessionId')
      localStorage.removeItem('participantId')
      localStorage.removeItem('participantKey')
      localStorage.removeItem('message_history')
      localStorage.removeItem('connectedToAgent')
      localStorage.removeItem('eligibleChat')
      localStorage.removeItem('limits')

      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage('TerminationHappened')
        this.broadcastChannel.close()
      }

      // Reset the dark chat flag when chat is ended
      if (this.darkProdFlag) {
        this.setDarkProdFlag(false)
        this.setDarkProdFlag(this.getCookie?.('darkChatGatewayProd') === 'true' && process.env.VUE_APP_DARK_MODE === 'true')
      }
      // Reset the unread messages count when the chat is ended
      this.setUnreadMessages(0)
      // Recheck the eligibility when session is terminated
      this.$emit('recheckEligibility')
    },
    closeWidgetEvent (close) {
      this.setChatMinimized(false)
      if (this.visibilityOfPrechatForm === false && !this.chatStarted) {
        this.cancelToaster()
      }
      if (!this.chatStarted) {
        this.cancelChat()
        return
      }

      if (!this.endConversation) {
        this.showCloseModal = true
        this.showAdditionalModal = false
      } else if (close && this.endConversation) {
        this.messages.length = 0
        this.setMessageHistory('')

        this.showCloseModal = false
        this.showAdditionalModal = false
        this.$emit('closeChat')
        this.endConversation = false
        this.cancelChat()
      } else {
        this.endChat(this.endConversation)
      }
    },
    updateUserActivity () {
      const time = Date.now()
      this.setLastActivityTime(time)
      if (this.broadcastChannel && this.sessionId) {
        this.broadcastChannel.postMessage(time)
      }
    },
    handleSessionExpiration () {
      const checkActivity = async () => {
        const currentTime = Date.now()
        const lastActivityTime = parseInt(this.lastActivityTime, 10)

        if (lastActivityTime) {
          const elapsedTime = currentTime - lastActivityTime

          if (elapsedTime >= this.sessionTimeout) {
            this.setChatMinimized(false)
            // User has been inactive for the sessionTimeout duration
            this.showEndSessionModal = true // Show the modal
            setTimeout(async () => {
              this.showEndSessionModal = false // Hide the modal after 10 seconds
              await this.endChat() // Call your logout or session expiration function
            }, 2000) // Wait for 2 seconds to hide the modal and call endChat
            clearInterval(this.intervalIdTermination) // Stop the interval
          }
        }
      }

      this.intervalIdTermination = setInterval(checkActivity, 10000) // Check activity every 10 seconds

      // Call checkActivity immediately to start the monitoring
      checkActivity()
    },
    submitMidChatForm (data) {
      this.MidChatFormSumitted = true
      this.webSocket.send(JSON.stringify({ dataCollectionFormsData: data?.form, sessionId: this.sessionId, event: 'DATA_COLLECTION_FORM' }))
    },
    proccedToPrechat () {
      clearTimeout(this.toasterTimeout)
      this.visibilityOfPrechatForm = true
      // localStorage.setItem('visibilityOfPrechatForm', true)
    },
    handleBeforeUnload () {
      if (this.endConversation) {
        this.messages.length = 0
        this.setMessageHistory('')
        this.showCloseModal = false
        this.$emit('closeChat')
        this.handleCloseByUser = true
        this.clearDataCollectionStatuses()
        this.clearDataCollectionForms()
      }
    },
    startTyping () {
      clearTimeout(this.typingTimer) // Clear any existing timer

      // Set a timer to send typing event after 3 seconds
      this.typingTimer = setTimeout(() => {
        this.sendTypingEvent()
      }, 1000)
    },
    stopTyping () {
      clearTimeout(this.typingTimer)
    },
    sendTypingEvent () {
      if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
        this.webSocket.send(JSON.stringify({
          event: 'TYPING',
          participantKey: this.participantKey,
          participantId: this.participantId,
          sessionId: this.sessionId
        }))
      }
    },
    checkMessagesValidity (messages) {
      let foundChips = false

      for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i]

        if (message.type === 'CHIPS') {
          foundChips = true
          messages = messages.slice(i + 1)
          break
        }
      }

      if (foundChips) {
        for (let i = 0; i < messages.length; i++) {
          if (messages[i].type !== 'SYSTEM') {
            return false
          }
        }
        return true
      }

      return false
    }
  }
}
</script>

<style lang="sass">
@import '@/style/theme.sass'
@import '@/style/ChatContainer.sass'
</style>
